import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { SempadanRalat } from "./components/SempadanRalat";
import "./App.css";

/* Laman awam dimuat serta-merta supaya pintu masuk utama pantas */
import Landing from "./pages/Landing";
import Login from "./pages/Login";

/* Halaman lain dipecahkan kepada chunk berasingan (code splitting).
   Ini memastikan pelawat laman awam tidak perlu memuat turun kod dashboard,
   peta Leaflet dan carta Recharts. */
const Privasi = lazy(() => import("./pages/Privasi"));
const Terma = lazy(() => import("./pages/Terma"));
const Hubungi = lazy(() => import("./pages/Hubungi"));
const AduanAwam = lazy(() => import("./pages/AduanAwam"));
const BantuanAwam = lazy(() => import("./pages/BantuanAwam"));
const ProgramAwam = lazy(() => import("./pages/ProgramAwam"));
const SukarelawanAwam = lazy(() => import("./pages/SukarelawanAwam"));
const CadanganAwam = lazy(() => import("./pages/CadanganAwam"));
const Semak = lazy(() => import("./pages/Semak"));
const PosterQr = lazy(() => import("./pages/PosterQr"));
const Layout = lazy(() => import("./components/Layout").then((m) => ({ default: m.Layout })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CommandCenter = lazy(() => import("./pages/CommandCenter"));
const Komitmen = lazy(() => import("./pages/Komitmen"));
const Agensi = lazy(() => import("./pages/Agensi"));
const DaerahList = lazy(() => import("./pages/DaerahList"));
const DaerahDetail = lazy(() => import("./pages/DaerahDetail"));
const MasalahPage = lazy(() => import("./pages/MasalahPage"));
const Infografik = lazy(() => import("./pages/Infografik"));
const WarRoom = lazy(() => import("./pages/WarRoom"));
const Laporan = lazy(() => import("./pages/Laporan"));
const Prestasi = lazy(() => import("./pages/Prestasi"));
const Rakyat = lazy(() => import("./pages/Rakyat"));
const TrackerAduan = lazy(() => import("./pages/TrackerAduan"));
const Kanban = lazy(() => import("./pages/Kanban"));
const KawasanZon = lazy(() => import("./pages/KawasanZon"));
const OrangCrm = lazy(() => import("./pages/OrangCrm"));
const Programa = lazy(() => import("./pages/Programa"));
const PembantuAi = lazy(() => import("./pages/PembantuAi"));
const KotakCadangan = lazy(() => import("./pages/KotakCadangan"));
const Panduan = lazy(() => import("./pages/Panduan"));
const SumberData = lazy(() => import("./pages/SumberData"));
const LogTindakan = lazy(() => import("./pages/LogTindakan"));
const Pengguna = lazy(() => import("./pages/Pengguna"));

const Memuat = () => (
  <div className="flex min-h-screen items-center justify-center bg-white">
    <p className="font-mono-plex text-xs uppercase tracking-[0.28em] text-[#4B5563]">Memuatkan</p>
  </div>
);

const Lindung = ({ children, adminSahaja = false, tanpaLayout = false }) => {
  const { pengguna, memuat } = useAuth();
  if (memuat || pengguna === null) return <Memuat />;
  if (!pengguna) return <Navigate to="/login" replace />;
  if (adminSahaja && pengguna.peranan !== "admin")
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#072F4A]">
            Akses ditolak
          </h1>
          <p className="mt-2 text-sm text-[#4B5563]">Halaman ini hanya untuk admin kempen.</p>
        </div>
      </Layout>
    );
  return tanpaLayout ? children : <Layout>{children}</Layout>;
};

const HanyaTamu = ({ children }) => {
  const { pengguna, memuat } = useAuth();
  if (memuat || pengguna === null) return <Memuat />;
  if (pengguna) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SempadanRalat>
        <Suspense fallback={<Memuat />}>
          <Routes>
            {/* Laman awam, terbuka tanpa log masuk */}
            <Route path="/" element={<Landing />} />
            <Route path="/dasar-privasi" element={<Privasi />} />
            <Route path="/terma" element={<Terma />} />
            <Route path="/hubungi" element={<Hubungi />} />
            <Route path="/aduan" element={<AduanAwam />} />
            <Route path="/bantuan" element={<BantuanAwam />} />
            <Route path="/program" element={<ProgramAwam />} />
            <Route path="/sukarelawan" element={<SukarelawanAwam />} />
            <Route path="/cadangan" element={<CadanganAwam />} />
            <Route path="/semak" element={<Semak />} />
            <Route path="/poster" element={<PosterQr />} />
            <Route path="/login" element={<HanyaTamu><Login /></HanyaTamu>} />

            {/* Perlu log masuk */}
            <Route path="/warroom" element={<Lindung tanpaLayout><WarRoom /></Lindung>} />
            <Route path="/laporan" element={<Lindung tanpaLayout><Laporan /></Lindung>} />
            <Route path="/dashboard" element={<Lindung><Dashboard /></Lindung>} />
            <Route path="/kesihatan" element={<Lindung><CommandCenter /></Lindung>} />
            <Route path="/komitmen" element={<Lindung><Komitmen /></Lindung>} />
            <Route path="/agensi" element={<Lindung><Agensi /></Lindung>} />
            <Route path="/daerah" element={<Lindung><DaerahList /></Lindung>} />
            <Route path="/daerah/:id" element={<Lindung><DaerahDetail /></Lindung>} />
            <Route path="/masalah" element={<Lindung><MasalahPage /></Lindung>} />
            <Route path="/infografik" element={<Lindung><Infografik /></Lindung>} />
            <Route path="/prestasi" element={<Lindung><Prestasi /></Lindung>} />
            <Route path="/rakyat" element={<Lindung><Rakyat /></Lindung>} />
            <Route path="/orang" element={<Lindung><OrangCrm /></Lindung>} />
            <Route path="/programa" element={<Lindung><Programa /></Lindung>} />
            <Route path="/ai" element={<Lindung><PembantuAi /></Lindung>} />
            <Route path="/kotak-cadangan" element={<Lindung><KotakCadangan /></Lindung>} />
            <Route path="/kanban" element={<Lindung><Kanban /></Lindung>} />
            <Route path="/kawasan" element={<Lindung><KawasanZon /></Lindung>} />
            <Route path="/panduan" element={<Lindung><Panduan /></Lindung>} />
            <Route path="/sumber-data" element={<Lindung><SumberData /></Lindung>} />
            <Route path="/aduan/:rujukan" element={<Lindung><TrackerAduan /></Lindung>} />
            <Route path="/log" element={<Lindung adminSahaja><LogTindakan /></Lindung>} />
            <Route path="/pengguna" element={<Lindung adminSahaja><Pengguna /></Lindung>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </SempadanRalat>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
